import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  GraduationCap, Loader2, CheckCircle2, Sparkles, Coins, ArrowRightLeft,
} from 'lucide-react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';

// Mirrors backend WELCOME_CREDITS. Hard-coded here is fine: it is a
// marketing freebie, not a configurable value, and the server is still
// the source of truth.
const WELCOME_CREDITS = 2;

function AcUkCopy() {
  return (
    <p className="text-xs text-stone-500 leading-relaxed">
      Only `.ac.uk` university email addresses are accepted. Credits earned in the Co-Op can be spent
      with other verified students only and can never be purchased with money.
    </p>
  );
}

/**
 * StudentVerificationCard
 *
 * Lets any logged-in user link their `.ac.uk` university email to
 * unlock the Zuriya Co-Op, claim 2 welcome credits, and (once verified)
 * toggle dual-mode so they can act as both client and provider.
 */
export default function StudentVerificationCard() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ is_student: false, credits: 0, dual_mode_active: false });
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [dualSaving, setDualSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/co-op/status');
      setStatus(res.data);
    } catch {
      // Non-blocking; the card still renders in its "unverified" state.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSendLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await api.post('/co-op/verify-student/start', { student_email: email.trim() });
      toast.success('Verification link sent', {
        description: `Check ${email.trim()} and click the link within 24 hours.`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send the verification link.');
    } finally {
      setSending(false);
    }
  };

  const handleToggleDual = async (next) => {
    setDualSaving(true);
    try {
      await api.post('/co-op/dual-mode', { active: next });
      setStatus((s) => ({ ...s, dual_mode_active: next }));
      toast.success(next ? 'Dual-mode on' : 'Dual-mode off');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update dual-mode.');
    } finally {
      setDualSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-6 flex items-center gap-3 text-stone-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your Co-Op status...
      </div>
    );
  }

  if (status.is_student) {
    return (
      <section
        className="rounded-xl border-2 border-[#D4AF37]/40 bg-[#FAF8F2] p-6"
        data-testid="co-op-verified-card"
      >
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <GraduationCap className="h-5 w-5 text-[#8a7320]" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Co-Op member</h3>
              <p className="text-xs text-stone-500">
                Verified <span className="font-mono">{status.student_email}</span>
              </p>
            </div>
          </div>
          <div
            className="flex items-center gap-2 bg-white border border-[#D4AF37]/50 rounded-lg px-3 py-2"
            data-testid="co-op-credits-badge"
          >
            <Coins className="h-4 w-4 text-[#D4AF37]" />
            <span className="font-mono text-sm text-[#1A1A1A]">
              <span className="font-semibold">{status.credits}</span> credits
            </span>
          </div>
        </header>

        <div
          className="mt-5 flex items-center justify-between gap-4 pt-4 border-t border-stone-200"
          data-testid="co-op-dual-mode-row"
        >
          <div className="flex items-center gap-2.5">
            <ArrowRightLeft className="h-4 w-4 text-stone-500" />
            <div>
              <p className="text-sm font-medium text-[#1A1A1A]">Dual-mode (client + provider)</p>
              <p className="text-xs text-stone-500">
                Act as both client and provider in your dashboard.
              </p>
            </div>
          </div>
          <Switch
            checked={!!status.dual_mode_active}
            onCheckedChange={handleToggleDual}
            disabled={dualSaving}
            data-testid="co-op-dual-mode-toggle"
          />
        </div>
      </section>
    );
  }

  return (
    <section
      className="rounded-xl border border-stone-200 bg-white p-6"
      data-testid="co-op-verify-card"
    >
      <header className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-stone-100 flex items-center justify-center">
          <GraduationCap className="h-5 w-5 text-stone-700" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">
            Join the Zuriya Co-Op
          </h3>
          <p className="text-sm text-stone-600 mt-0.5 leading-relaxed">
            Verify your UK university email to unlock the student Co-Op tier and claim{' '}
            <strong>{WELCOME_CREDITS} welcome credits</strong>.
          </p>
        </div>
      </header>

      <form onSubmit={handleSendLink} className="mt-5 flex flex-col sm:flex-row gap-2.5">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.name@university.ac.uk"
          className="flex-1 h-11"
          required
          data-testid="co-op-verify-email-input"
        />
        <Button
          type="submit"
          disabled={sending}
          className="h-11 bg-[#1A1A1A] text-white hover:bg-[#2a2a2a] px-5"
          data-testid="co-op-verify-send-btn"
        >
          {sending ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending...</>
          ) : (
            <><Sparkles className="h-4 w-4 mr-2" /> Send verification link</>
          )}
        </Button>
      </form>

      <ul className="mt-5 space-y-2 text-sm text-stone-700">
        <li className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
          2 welcome credits on first verification
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
          Earn more credits by providing services to other students
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
          Dual-mode lets you book as a client and offer services as a provider
        </li>
      </ul>

      <div className="mt-4 pt-4 border-t border-stone-100">
        <AcUkCopy />
      </div>
    </section>
  );
}
